import axios from 'axios';
import LangService from './lang';
import API_URL from '../constants/api';
import { Cookies } from 'react-cookie';
import { setCookies } from '../store/auth/actions';
import {JSON} from "../constants/format";

const cookies = new Cookies();

const axiosInstance = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
});
axiosInstance.interceptors.response.use(
    response => {
        return response;
    },
    async (error) => {
        if (error.response && error.response.status === 401) {
            try {
                const refreshToken = cookies.get('refresh');
                if (refreshToken) {
                    const service = new APIService();
                    const response = await service.refresh({ refresh: refreshToken });
                    setCookies(response, false);
                    error.config.headers['Authorization'] = `Bearer ${response.token}`;
                    return axiosInstance(error.config);
                }
            } catch (error) {
                throw error;
            }
        }
        return Promise.reject(error);
    },
);

class APIService {

    headers(lang, token = null) {
        let header = {
            'Accept-Language': lang,
        };
        if (
            token
            && (
                header.Authorization === undefined
                || header.Authorization === null
                || header.Authorization === ''
            )
        ) {
            header.Authorization = `Bearer ${token}`;
        }
        return header;
    }

    // Authentication

    forgotPassword(email) {
        return axios
            .post(`${API_URL}/admins/lostpasswords`, { email })
            .then(response => {
                return response.data;
            });
    }

    login({
        login,
        password,
        rememberme,
    }) {
        return axios
            .post(`${API_URL}/login`, {
                username: login,
                password: password,
                rememberme,
            })
            .then(response => {
                return response.data;
            });
    }

    refresh({ refresh }) {
        return axios
            .post(`${API_URL}/token/refresh`, { 'refresh_token': refresh })
            .then(response => {
                return response.data;
            });
    }

    // Applications

    getApp(id, token) {
        return axiosInstance
            .get(`${API_URL}/applications/${id}`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    getApplications(token) {
        return axiosInstance
            .get(`${API_URL}/applications`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(function (response) {
                return response.data;
            });
    }

    postApp(app, token) {
        return axiosInstance
            .post(`${API_URL}/applications`, app, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    putApp(id, app, token) {
        return axiosInstance
            .put(`${API_URL}/applications/${id}`, app, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    postAppConfigurations(id, content, token, isFile = false) {
        let headers = { ...this.headers(LangService.defaultLang, token) };
        if (isFile) {
            headers['Content-Type'] = 'multipart/form-data';
        }
        return axiosInstance
            .post(`${API_URL}/applications/${id}/configurations`, content, {
                headers: headers,
            })
            .then(response => {
                return response.data;
            });
    }

    deleteApp(id, token) {
        return axiosInstance
            .delete(`${API_URL}/applications/${id}`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return true;
            });
    }

    deleteAppAdmin(appId, adminId, token) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/admins/${adminId}`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    addAppAdmin(appId, adminId, token) {
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/admins`, { admin_id: adminId }, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    postAppPicture(appId, picture, token, onUploadProgress = null) {
        let data = new FormData();
        data.append('picture', picture);

        return axiosInstance
            .post(`${API_URL}/applications/${appId}/picture`, data, {
                headers: this.headers(LangService.defaultLang, token),
                onUploadProgress: onUploadProgress,
            })
            .then(response => {
                return response.data;
            });
    }

    deleteAppPicture(appId, token) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/picture`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    // Description medias (images of the WYSIWYG editor)

    postDescriptionMedia(picture, token) {
        let data = new FormData();
        data.append('picture', picture);

        return axiosInstance
            .post(`${API_URL}/descriptions/medias`, data, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    // Admins

    getAdmin(id, token) {
        return axiosInstance
            .get(`${API_URL}/admins/${id}`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    getAdmins(token) {
        return axiosInstance
            .get(`${API_URL}/admins`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    addAdmin(user, token) {
        return axiosInstance
            .post(`${API_URL}/admins`, user, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    editAdmin(user, token) {
        return axiosInstance
            .put(`${API_URL}/admins/${user.id}`, user, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    deleteAdmin(admin, token) {
        return axiosInstance
            .delete(`${API_URL}/admins/${admin.id}`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return true;
            });
    }

    // Reports

    getReports(app, token, lang) {
        return axiosInstance
            .get(`${API_URL}/applications/${app.id}/reports`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    getReport(appId, reportId, token, lang) {
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/reports/${reportId}`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    putReport(appId, report, token, lang) {
        return axiosInstance
            .put(`${API_URL}/applications/${appId}/reports/${report.id}`, report, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    postReportItem(appId, reportId, item, token, lang) {
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/reports/${reportId}/items`, item, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    postReportItemMedia(appId, reportId, itemId, picture, token, lang) {
        let data = new FormData();
        data.append('picture', picture);
        return axiosInstance
            .post(
                `${API_URL}/applications/${appId}/reports/${reportId}/items/${itemId}/picture`,
                data,
                {
                    headers: this.headers(lang, token),
                },
            )
            .then(response => {
                return response.data;
            });
    }

    deleteReport(appId, pointId, token, lang) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/reports/${pointId}`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return true;
            });
    }

    // News

    getNews(app, token, lang) {
        return axiosInstance
            .get(`${API_URL}/applications/${app.id}/news?order=desc&sortBy=createdAt`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    getOneNews(appId, pointId, token, lang) {
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/news/${pointId}`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    deleteOneNews(appId, pointId, token, lang) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/news/${pointId}`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return true;
            });
    }

    putOneNews(appId, point, token, lang) {
        return axiosInstance
            .put(`${API_URL}/applications/${appId}/news/${point.id}`, point, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    postOneNews(appId, point, token, lang) {
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/news`, point, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    // Points

    getPoints(app, token, lang, format, allResponse) {
        let params = [];
        if (format) {
            params.push(`format=${format}`);
        }
        return axiosInstance
            .get(`${API_URL}/applications/${app.id}/points` + (
                params.length > 0 ? `?${params.join('&')}` : ''
            ), {
                headers: this.headers(lang, token),
                responseType: format && format !== JSON ? 'blob' : JSON,
            })
            .then(response => {
                if (allResponse) {
                    return response;
                }
                return response.data;
            });
    }

    getPoint(appId, pointId, token, lang, format, allResponse) {
        let params = [];
        if (format) {
            params.push(`format=${format}`);
        }
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/points/${pointId}` + (
                params.length > 0 ? `?${params.join('&')}` : ''
            ), {
                headers: this.headers(lang, token),
                responseType: format && format !== JSON ? 'blob' : JSON,
            })
            .then(response => {
                if (allResponse) {
                    return response;
                }
                return response.data;
            });
    }

    deletePoint(appId, pointId, token, lang) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/points/${pointId}`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return true;
            });
    }

    putPoint(appId, point, token, lang) {
        // for unknown reason, the point form transform lat or lng to string
        const pointToSend = {
            ...point,
            latitude: Number.parseFloat(point.latitude),
            longitude: Number.parseFloat(point.longitude),
        };
        return axiosInstance
            .put(`${API_URL}/applications/${appId}/points/${point.id}`, pointToSend, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    postPoint(appId, point, token, lang) {
        // for unknown reason, the point form transform lat or lng to string
        const pointToSend = {
            ...point,
            latitude: Number.parseFloat(point.latitude),
            longitude: Number.parseFloat(point.longitude),
        };
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/points`, pointToSend, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    // Trails

    getTrails(app, token, lang) {
        return axiosInstance
            .get(`${API_URL}/applications/${app.id}/trails`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    getTrail(appId, trailId, token, lang) {
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/trails/${trailId}`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    deleteTrail(appId, trailId, token, lang) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/trails/${trailId}`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return true;
            });
    }

    putTrail(appId, trail, token, lang) {
        return axiosInstance
            .put(`${API_URL}/applications/${appId}/trails/${trail.id}`, trail, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    postTrail(appId, trail, token, lang) {
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/trails`, trail, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    postTrailGpx(appId, trailId, file, token, lang, onUploadProgress = null) {
        let data = new FormData();
        data.append('gpx', file);

        return axiosInstance
            .post(`${API_URL}/applications/${appId}/trails/${trailId}/gpx`, data, {
                headers: this.headers(lang, token),
                onUploadProgress: onUploadProgress,
            })
            .then(response => {
                return response.data;
            });
    }

    postTrailKml(appId, trailId, file, token, lang, onUploadProgress = null) {
        let data = new FormData();
        data.append('kml', file);

        return axiosInstance
            .post(`${API_URL}/applications/${appId}/trails/${trailId}/kml`, data, {
                headers: this.headers(lang, token),
                onUploadProgress: onUploadProgress,
            })
            .then(response => {
                return response.data;
            });
    }

    postTrailGeojson(appId, trailId, file, token, lang, onUploadProgress = null) {
        let data = new FormData();
        data.append('geojson', file);

        return axiosInstance
            .post(`${API_URL}/applications/${appId}/trails/${trailId}/geojson`, data, {
                headers: this.headers(lang, token),
                onUploadProgress: onUploadProgress,
            })
            .then(response => {
                return response.data;
            });
    }

    // Medias

    getPointMedia(appId, pointId, mediaId, token, lang) {
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/points/${pointId}/medias/${mediaId}`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    deletePointMedia(appId, pointId, media, token, lang) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/points/${pointId}/medias/${media.id}`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return true;
            });
    }

    putPointMedia(appId, pointId, media, token, lang) {
        const mediaToSend = {
            ...media,
            order: Number.parseInt(media.order, 10),
        };
        return axiosInstance
            .put(
                `${API_URL}/applications/${appId}/points/${pointId}/medias/${media.id}`,
                mediaToSend,
                { headers: this.headers(lang, token) },
            )
            .then(response => {
                return response.data;
            });
    }

    postPointMedia(appId, pointId, media, token, lang, onUploadProgress = null) {
        let data = new FormData();
        data.append('media', media);
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/points/${pointId}/medias`, data, {
                headers: this.headers(lang, token),
                onUploadProgress: onUploadProgress,
            })
            .then(response => {
                return response.data;
            });
    }

    postPointLinkMedia(appId, pointId, link, token, lang) {
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/points/${pointId}/medias`, { link }, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    getTrailMedia(appId, trailId, mediaId, token, lang) {
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/trails/${trailId}/medias/${mediaId}`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    deleteTrailMedia(appId, trailId, media, token, lang) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/trails/${trailId}/medias/${media.id}`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return true;
            });
    }

    putTrailMedia(appId, trailId, media, token, lang) {
        const mediaToSend = {
            ...media,
            order: Number.parseInt(media.order, 10),
        };
        return axiosInstance
            .put(
                `${API_URL}/applications/${appId}/trails/${trailId}/medias/${media.id}`,
                mediaToSend,
                {
                    headers: this.headers(lang, token),
                },
            )
            .then(response => {
                return response.data;
            });
    }

    postTrailMedia(appId, trailId, media, token, lang, onUploadProgress = null) {
        let data = new FormData();
        data.append('media', media);

        return axiosInstance
            .post(`${API_URL}/applications/${appId}/trails/${trailId}/medias`, data, {
                headers: this.headers(lang, token),
                onUploadProgress: onUploadProgress,
            })
            .then(response => {
                return response.data;
            });
    }

    postTrailLinkMedia(appId, trailId, link, token, lang) {
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/trails/${trailId}/medias`, { link }, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    postReportMedia(appId, reportId, media, token, lang, onUploadProgress = null) {
        let data = new FormData();
        data.append('media', media);
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/reports/${reportId}/medias`, data, {
                headers: this.headers(lang, token),
                onUploadProgress: onUploadProgress,
            })
            .then(response => {
                return response.data;
            });
    }

    getOneNewsMedia(appId, pointId, mediaId, token, lang) {
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/news/${pointId}/medias/${mediaId}`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    deleteOneNewsMedia(appId, pointId, media, token, lang) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/news/${pointId}/medias/${media.id}`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return true;
            });
    }

    putOneNewsMedia(appId, pointId, media, token, lang) {
        const mediaToSend = {
            ...media,
            order: Number.parseInt(media.order, 10),
        };
        return axiosInstance
            .put(
                `${API_URL}/applications/${appId}/news/${pointId}/medias/${media.id}`,
                mediaToSend,
                {
                    headers: this.headers(lang, token),
                },
            )
            .then(response => {
                return response.data;
            });
    }

    postOneNewsMedia(appId, newsId, media, token, lang, onUploadProgress = null) {
        let data = new FormData();
        data.append('media', media);
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/news/${newsId}/medias`, data, {
                headers: this.headers(lang, token),
                onUploadProgress: onUploadProgress,
            })
            .then(response => {
                return response.data;
            });
    }

    postOneNewsLinkMedia(appId, newsId, link, token, lang) {
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/news/${newsId}/medias`, { link }, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    getPageMedia(appId, pageId, mediaId, token, lang) {
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/pages/${pageId}/medias/${mediaId}`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    deletePageMedia(appId, pageId, media, token, lang) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/pages/${pageId}/medias/${media.id}`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return true;
            });
    }

    putPageMedia(appId, pageId, media, token, lang) {
        const mediaToSend = {
            ...media,
            order: Number.parseInt(media.order, 10),
        };
        return axiosInstance
            .put(
                `${API_URL}/applications/${appId}/pages/${pageId}/medias/${media.id}`,
                mediaToSend,
                {
                    headers: this.headers(lang, token),
                },
            )
            .then(response => {
                return response.data;
            });
    }

    postPageMedia(appId, pageId, media, token, lang, onUploadProgress = null) {
        let data = new FormData();
        data.append('media', media);
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/pages/${pageId}/medias`, data, {
                headers: this.headers(lang, token),
                onUploadProgress: onUploadProgress,
            })
            .then(response => {
                return response.data;
            });
    }

    postPageLinkMedia(appId, pageId, link, token, lang) {
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/pages/${pageId}/medias`, { link }, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    // Types

    getType(appId, typeId, token) {
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/types/${typeId}`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    getTypes(appId, token, lang) {
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/types`, {
                headers: this.headers(lang, token),
            })
            .then(function (response) {
                return response.data;
            });
    }

    postType(appId, type, token, lang) {
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/types`, type, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    putType(appId, type, token, lang) {
        return axiosInstance
            .put(`${API_URL}/applications/${appId}/types/${type.id}`, type, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    postTypePicture(appId, typeId, picture, token, onUploadProgress = null) {
        let data = new FormData();
        data.append('picture', picture);

        return axiosInstance
            .post(`${API_URL}/applications/${appId}/types/${typeId}/picture`, data, {
                headers: this.headers(LangService.defaultLang, token),
                onUploadProgress: onUploadProgress,
            })
            .then(response => {
                return response.data;
            });
    }

    deleteTypePicture(appId, typeId, token) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/types/${typeId}/picture`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    deleteType(appId, typeId, token) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/types/${typeId}`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return true;
            });
    }

    // Report subjects

    getReportSubject(appId, subjectId, token) {
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/reportsubjects/${subjectId}`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    getReportSubjects(appId, token) {
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/reportsubjects`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(function (response) {
                return response.data;
            });
    }

    postReportSubject(appId, subject, token) {
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/reportsubjects`, subject, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    putReportSubject(appId, subject, token) {
        return axiosInstance
            .put(`${API_URL}/applications/${appId}/reportsubjects/${subject.id}`, subject, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    postReportSubjectPicture(appId, subjectId, picture, token, onUploadProgress = null) {
        let data = new FormData();
        data.append('picture', picture);

        return axiosInstance
            .post(`${API_URL}/applications/${appId}/reportsubjects/${subjectId}/picture`, data, {
                headers: this.headers(LangService.defaultLang, token),
                onUploadProgress: onUploadProgress,
            })
            .then(response => {
                return response.data;
            });
    }

    deleteReportSubjectPicture(appId, subjectId, token) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/reportsubjects/${subjectId}/picture`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    deleteReportSubject(appId, subjectId, token) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/reportsubjects/${subjectId}`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return true;
            });
    }

// Media Types

    getMediaType(appId, mediaTypeId, token) {
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/mediatypes/${mediaTypeId}`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    getMediaTypes(appId, token) {
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/mediatypes`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(function (response) {
                return response.data;
            });
    }

    postMediaType(appId, mediaType, token) {
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/mediatypes`, mediaType, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    putMediaType(appId, mediaType, token) {
        return axiosInstance
            .put(`${API_URL}/applications/${appId}/mediatypes/${mediaType.id}`, mediaType, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    postMediaTypePicture(appId, mediaTypeId, picture, token, onUploadProgress = null) {
        let data = new FormData();
        data.append('picture', picture);

        return axiosInstance
            .post(`${API_URL}/applications/${appId}/mediatypes/${mediaTypeId}/picture`, data, {
                headers: this.headers(LangService.defaultLang, token),
                onUploadProgress: onUploadProgress,
            })
            .then(response => {
                return response.data;
            });
    }

    deleteMediaTypePicture(appId, mediaTypeId, token) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/mediatypes/${mediaTypeId}/picture`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    deleteMediaType(appId, mediaTypeId, token) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/mediatypes/${mediaTypeId}`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return true;
            });
    }

    // Custom Fields

    getCustomField(appId, customFieldId, token) {
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/customfields/${customFieldId}`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    getCustomFields(appId, token) {
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/customfields`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(function (response) {
                return response.data;
            });
    }

    postCustomField(appId, customField, token) {
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/customfields`, customField, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    putCustomField(appId, customField, token) {
        return axiosInstance
            .put(`${API_URL}/applications/${appId}/customfields/${customField.id}`, customField, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    deleteCustomField(appId, customFieldId, token) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/customfields/${customFieldId}`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return true;
            });
    }

    // Comments

    getComments(appId, token) {
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/comments`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(function (response) {
                return response.data;
            });
    }

    putComment(appId, commentId, validated, token) {
        return axiosInstance
            .put(
                `${API_URL}/applications/${appId}/comments/${commentId}`,
                { is_validate: validated },
                {
                    headers: this.headers(LangService.defaultLang, token),
                },
            )
            .then(response => {
                return response.data;
            });
    }

    deleteComment(appId, commentId, token) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/comments/${commentId}`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return true;
            });
    }

    // Push

    postPushGroup(appId, pushGroup, token) {
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/pushgroups`, pushGroup, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    deletePushGroup(appId, pushGroup, token) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/pushgroups/${pushGroup}`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    postPushSound(appId, pushSound, token) {
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/pushsounds`, pushSound, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    deletePushSound(appId, pushSound, token) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/pushsounds/${pushSound}`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    postPush(appId, push, token) {
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/push`, push, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    getSubscribedPush(appId, push, token) {
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/subscribed/push`, push, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    //Page

    getPages(appId, token, lang) {
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/pages`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    getPage(appId, pageId, token, lang) {
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/pages/${pageId}`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    postPage(appId, page, token, lang) {
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/pages`, page, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    putPage(appId, page, token, lang) {
        return axiosInstance
            .put(`${API_URL}/applications/${appId}/pages/${page.id}`, page, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    deletePage(appId, pageId, token, lang) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/pages/${pageId}`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    // dynamicLink
    getDynamicLinkQrCode(appId, type, typeId, format, token, lang) {
        if (format === undefined) {
            format = 'png';
        }
        const parameters = `?entity_id=${typeId}&entity_name=${type}&format=${format}`;

        return axiosInstance.get(`${API_URL}/applications/${appId}/qrcode${parameters}`, {
            headers: this.headers(lang, token),
            responseType: 'blob',
        })
            .then(response => {
                return response.data;
            });
    }

    // road book configuration
    getRoadBookConfigurations(appId, withDisabled, token, lang) {
        let url = `${API_URL}/applications/${appId}/road-book-configurations`;
        if (!withDisabled) {
            url = url + '?isActivated=1';
        }
        return axiosInstance
            .get(url, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    postRoadBookConfiguration(appId, roadBookConfiguration, token, lang) {
        let url = `${API_URL}/applications/${appId}/road-book-configurations`;
        return axiosInstance
            .post(url, roadBookConfiguration, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    putRoadBookConfiguration(appId, roadBookConfiguration, token, lang) {
        let url = `${API_URL}/applications/${appId}/road-book-configurations/${roadBookConfiguration.id}`;
        return axiosInstance
            .put(url, roadBookConfiguration, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    deleteRoadBookConfiguration(appId, roadBookConfiguration, token, lang) {
        let url = `${API_URL}/applications/${appId}/road-book-configurations/${roadBookConfiguration.id}`;
        return axiosInstance
            .delete(url, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                if (response.status === 200) {
                    // if a super admin disables an RBA, the API returns it
                    return response.data;
                }
                return null;
            });
    }

    // achievements
    getAchievements(app, token, lang) {
        return axiosInstance
            .get(`${API_URL}/applications/${app.id}/achievements`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    postAchievement(app, achievement, token, lang) {
        return axiosInstance
            .post(`${API_URL}/applications/${app.id}/achievements`, achievement, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    putAchievement(app, achievement, token, lang) {
        return axiosInstance
            .put(
                `${API_URL}/applications/${app.id}/achievements/${achievement.id}`,
                achievement,
                { headers: this.headers(lang, token) },
            )
            .then(response => {
                return response.data;
            });
    }

    deleteAchievement(app, achievement, token, lang) {
        return axiosInstance
            .delete(
                `${API_URL}/applications/${app.id}/achievements/${achievement.id}`,
                { headers: this.headers(lang, token) },
            )
            .then(response => {
                return response.data;
            });
    }

    postAchievementPicture(app, achievement, picture, token, lang, onUploadProgress = null) {
        let data = new FormData();
        data.append('picture', picture);
        return axiosInstance.post(
            `${API_URL}/applications/${app.id}/achievements/${achievement.id}/picture`,
            data,
            {
                headers: this.headers(lang, token),
                onUploadProgress: onUploadProgress,
            },
        ).then(response => {
            return response.data;
        });
    }

    deleteAchievementPicture(app, achievement, token) {
        return axiosInstance.delete(
            `${API_URL}/applications/${app.id}/achievements/${achievement.id}/picture`,
            {
                headers: this.headers(LangService.defaultLang, token),
            },
        ).then(response => {
            return response.data;
        });
    }

    postAchievementUnearnedPicture(app, achievement, picture, token, lang, onUploadProgress = null) {
        let data = new FormData();
        data.append('picture', picture);
        return axiosInstance.post(
            `${API_URL}/applications/${app.id}/achievements/${achievement.id}/unearned-picture`,
            data,
            {
                headers: this.headers(lang, token),
                onUploadProgress: onUploadProgress,
            },
        ).then(response => {
            return response.data;
        });
    }

    deleteAchievementUnearnedPicture(app, achievement, token) {
        return axiosInstance.delete(
            `${API_URL}/applications/${app.id}/achievements/${achievement.id}/unearned-picture`,
            {
                headers: this.headers(LangService.defaultLang, token),
            },
        ).then(response => {
            return response.data;
        });
    }

    // user achievements

    getUserAchievements(app, token, lang, format, allResponse, limit, achievementId) {
        let params = [];
        if (format) {
            params.push(`format=${format}`);
        }
        if (limit) {
            params.push(`limit=${limit}`);
        }
        if (achievementId) {
            params.push(`achievementId=${achievementId}`);
        }

        return axiosInstance
            .get(
                `${API_URL}/applications/${app.id}/user-achievements` + (
                    params.length > 0 ? `?${params.join('&')}` : ''
                ),
                { headers: this.headers(lang, token) },
            )
            .then(response => {
                if (allResponse) {
                    return response;
                } else {
                    return response.data;
                }
            });
    }

    // quiz

    getQuizzes(app, token, lang) {
        return axiosInstance
            .get(`${API_URL}/applications/${app.id}/quizzes`, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    postQuiz(app, quiz, token, lang) {
        return axiosInstance
            .post(`${API_URL}/applications/${app.id}/quizzes`, quiz, {
                headers: this.headers(lang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    putQuiz(app, quiz, token, lang) {
        return axiosInstance
            .put(
                `${API_URL}/applications/${app.id}/quizzes/${quiz.id}`,
                quiz,
                { headers: this.headers(lang, token) },
            )
            .then(response => {
                return response.data;
            });
    }

    deleteQuiz(app, quiz, token, lang) {
        return axiosInstance
            .delete(
                `${API_URL}/applications/${app.id}/quizzes/${quiz.id}`,
                { headers: this.headers(lang, token) },
            )
            .then(response => {
                return response.data;
            });
    }

    postQuizPicture(app, quiz, picture, token, lang, onUploadProgress = null) {
        let data = new FormData();
        data.append('picture', picture);
        return axiosInstance.post(`${API_URL}/applications/${app.id}/quizzes/${quiz.id}/picture`, data, {
            headers: this.headers(lang, token),
            onUploadProgress: onUploadProgress,
        }).then(response => {
            return response.data;
        });
    }

    deleteQuizPicture(app, quiz, token, lang) {
        return axiosInstance.delete(`${API_URL}/applications/${app.id}/quizzes/${quiz.id}/picture`, {
            headers: this.headers(lang, token),
        }).then(response => {
            return response.data;
        });
    }

    postQuizQuestionPicture(app, quiz, question, picture, token, lang, onUploadProgress = null) {
        let data = new FormData();
        data.append('picture', picture);
        return axiosInstance.post(
            `${API_URL}/applications/${app.id}/quizzes/${quiz.id}/questions/${question.id}/picture`,
            data,
            {
                headers: this.headers(lang, token),
                onUploadProgress: onUploadProgress,
            },
        ).then(response => {
            return response.data;
        });
    }

    deleteQuizQuestionPicture(app, quiz, question, token, lang) {
        return axiosInstance.delete(
            `${API_URL}/applications/${app.id}/quizzes/${quiz.id}/questions/${question.id}/picture`,
            {
                headers: this.headers(lang, token),
            },
        ).then(response => {
            return response.data;
        });
    }

    // AccessTokens

    getAccessTokens(appId, token) {
        return axiosInstance
            .get(`${API_URL}/applications/${appId}/access-tokens`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    postAccessToken(appId, accessTokenName, token) {
        return axiosInstance
            .post(`${API_URL}/applications/${appId}/access-tokens`, accessTokenName, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }

    deleteAccessToken(appId, accessTokenId, token) {
        return axiosInstance
            .delete(`${API_URL}/applications/${appId}/access-tokens/${accessTokenId}`, {
                headers: this.headers(LangService.defaultLang, token),
            })
            .then(response => {
                return response.data;
            });
    }
}

export default new APIService();



// WEBPACK FOOTER //
// src/services/api.js